import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

interface FooterLink {
  key: string;
  href?: string;
  slug?: string;
}

export default function Footer() {
  const { t, i18n } = useTranslation();

  const resources: FooterLink[] = [
    { key: "nav.spreadsheet", href: "#categories" },
    { key: "nav.finds", href: "#categories" },
    { key: "nav.guides", href: "#guides" },
    { key: "nav.reviews", slug: "usfans-review" },
    { key: "nav.shipping", slug: "usfans-shipping-cost" },
    { key: "nav.coupons", slug: "usfans-coupon" },
    { key: "nav.warehouse", slug: "usfans-warehouse" },
    { key: "nav.safety", slug: "usfans-legit" },
    { key: "nav.faq", href: "#faq" },
  ];

  const company = ["footer.about", "footer.methodology", "footer.editorial", "footer.contact"];
  const legal = ["footer.privacy", "footer.terms", "footer.disclaimer"];

  return (
    <footer className="bg-accent-950 text-background-50">
      <div className="w-full px-4 md:px-6 py-14">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-5">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to={`/${i18n.language}`} className="flex items-center gap-2 cursor-pointer">
              <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-table-line text-white text-lg"></i>
              </span>
              <span className="font-heading text-lg font-bold text-background-50 whitespace-nowrap">
                USFans<span className="text-primary-400">Sheet</span>
              </span>
            </Link>
            <p className="mt-4 max-w-sm text-sm text-background-300 leading-relaxed">
              {t("footer.disclaimerText")}
            </p>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-background-100 mb-4">
              {t("footer.resources")}
            </h4>
            <ul className="space-y-2">
              {resources.map((r) => (
                <li key={r.key}>
                  {r.slug ? (
                    <Link
                      to={`/${i18n.language}/article/${r.slug}`}
                      className="text-sm text-background-300 hover:text-background-50 transition-colors cursor-pointer"
                    >
                      {t(r.key)}
                    </Link>
                  ) : (
                    <a
                      href={r.href}
                      className="text-sm text-background-300 hover:text-background-50 transition-colors cursor-pointer"
                    >
                      {t(r.key)}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-background-100 mb-4">
              {t("footer.company")}
            </h4>
            <ul className="space-y-2">
              {company.map((c) => (
                <li key={c}>
                  <a href="#" className="text-sm text-background-300 hover:text-background-50 transition-colors cursor-pointer">
                    {t(c)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal + Language */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-background-100 mb-4">
              {t("footer.legal")}
            </h4>
            <ul className="space-y-2">
              {legal.map((l) => (
                <li key={l}>
                  <a href="#" className="text-sm text-background-300 hover:text-background-50 transition-colors cursor-pointer">
                    {t(l)}
                  </a>
                </li>
              ))}
            </ul>
            <h4 className="text-sm font-bold uppercase tracking-wider text-background-100 mt-6 mb-2">
              {t("footer.language")}
            </h4>
            <LanguageSwitcher variant="dark" />
          </div>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-background-800 pt-6">
          <p className="text-xs text-background-400">
            © {new Date().getFullYear()} USFansSheet. {t("footer.rights")}
          </p>
          <p className="text-xs text-background-400">
            {t("footer.disclaimerText")}
          </p>
        </div>
      </div>
    </footer>
  );
}